<div class="shipment-edit">
  <div class="shipment-edit__content">
    <div class="shipment-edit__layout">
      <div class="shipment-edit__header">
        <div class="shipment-edit__heading">
          <h1 class="page-title">Edit Shipment</h1>
          <p class="page-subtitle">
            Move the shipment through setup, packing, dispatch, and delivery without mixing every operational task into one screen.
          </p>
        </div>
        <div class="shipment-edit__actions">
          <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
          @if (shipment && canEditSnapshot && activeStage === "snapshot") {
          <app-button type="button" [disabled]="isSaving || !isSnapshotValid" (click)="saveShipment()">
            {{ isSaving ? "Saving..." : "Save Snapshot" }}
          </app-button>
          }
        </div>
      </div>

      @if (isReady && shipment) {
      <div class="shipment-edit__hero">
        <div class="shipment-edit__hero-title">
          <span class="shipment-edit__hero-label">Shipment</span>
          <strong>{{ shipment.shipmentNo }}</strong>
        </div>
        <div class="shipment-edit__hero-meta">
          <span class="shipment-edit__hero-pill">
            <span class="material-symbols-outlined">flag</span>
            <span>{{ getStatusLabel(shipment.status) }}</span>
          </span>
          <span class="shipment-edit__hero-pill">
            <span class="material-symbols-outlined">apartment</span>
            <span>{{ shipmentPartnerLabel }}</span>
          </span>
          <span class="shipment-edit__hero-pill">
            <span class="material-symbols-outlined">route</span>
            <span>{{ shipment.routeSummary || (shipment.routeType === "Transit" ? "Transit shipment" : "Direct shipment") }}</span>
          </span>
          <span class="shipment-edit__hero-pill">
            <span class="material-symbols-outlined">task_alt</span>
            <span>{{ shipment.nextAction }}</span>
          </span>
        </div>
      </div>

      <div class="shipment-edit__stage-nav">
        @for (stage of getVisibleStages(); track stage; let index = $index) {
        <button
          type="button"
          class="shipment-edit__stage-btn"
          [class.shipment-edit__stage-btn--active]="activeStage === stage"
          [class.shipment-edit__stage-btn--disabled]="!isStageEnabled(stage)"
          [disabled]="!isStageEnabled(stage)"
          (click)="openStage(stage)">
          <span class="shipment-edit__stage-index">{{ index + 1 }}</span>
          <span>{{ getStageLabel(stage) }}</span>
        </button>
        }
      </div>

      <div class="shipment-edit__grid">
        <div class="shipment-edit__main">
          @if (activeStage === "snapshot") {
          <app-card>
            <form [formGroup]="form" class="shipment-edit__section">
              <div class="shipment-edit__section-header">
                <div>
                  <h2 class="shipment-edit__section-title">{{ getSnapshotSectionTitle() }}</h2>
                  <p class="shipment-edit__section-copy">{{ getSnapshotSectionCopy() }}</p>
                </div>
              </div>

              @if (canEditSnapshot || hasTransitStop) {
              <div class="shipment-edit__section shipment-edit__section--with-divider">
                <div class="shipment-edit__section-header">
                  <div>
                    <h2 class="shipment-edit__section-title">Transit Stop</h2>
                    <p class="shipment-edit__section-copy">
                      {{ isSnapshotHistoryMode ? "Saved relay stop used before final delivery." : "Add a relay partner only if the
                      shipment needs a handover before final delivery." }}
                    </p>
                  </div>
                  @if (!hasRouteStarted) { @if (!hasTransitStop) {
                  <app-button type="button" variant="secondary" (click)="addTransitStop()">Add Transit Stop</app-button>
                  } @else {
                  <app-button type="button" variant="secondary" (click)="removeTransitStop()">Remove Transit Stop</app-button>
                  } }
                </div>

                @if (hasTransitStop) {
                <div class="shipment-edit__transit-panel">
                  @if (!hasRouteStarted) {
                  <div class="shipment-edit__transit-fields">
                    <div class="shipment-edit__field">
                      <label for="shipment-pages-edit-shipmenteditpage-field-1" class="shipment-edit__label">Transit Partner</label>
                      <app-autocomplete
                        [prefetchOnInit]="true"
                        [options]="transitPartnerOptions"
                        [selectedValue]="selectedTransitPartnerId"
                        [minLength]="1"
                        [debounceMs]="300"
                        placeholder="Search transit partner..."
                        (searchChange)="searchTransitPartners($event)"
                        (selectionChange)="onTransitPartnerChange($event)"></app-autocomplete>
                    </div>

                    <div class="shipment-edit__field">
                      <label for="shipment-pages-edit-shipmenteditpage-field-2" class="shipment-edit__label"
                        >Transit Recipient</label
                      >
                      <app-autocomplete
                        [prefetchOnInit]="true"
                        [options]="transitRecipientOptions"
                        [selectedValue]="selectedTransitRecipientId"
                        [disabled]="!selectedTransitPartnerId"
                        [minLength]="0"
                        [debounceMs]="300"
                        placeholder="{{ selectedTransitPartnerId ? 'Search transit recipient...' : 'Select transit partner first' }}"
                        (searchChange)="searchTransitRecipients($event)"
                        (selectionChange)="onTransitRecipientChange($event)"></app-autocomplete>
                    </div>

                    @if (transitRecipientsAvailable === false) {
                    <div class="shipment-edit__empty shipment-edit__empty--inline">
                      No team leaders are linked to this partner in Relation Manager. Add one there before saving.
                    </div>
                    }

                    <div class="shipment-edit__field shipment-edit__field--compact">
                      <label for="shipment-pages-edit-shipmenteditpage-field-3" class="shipment-edit__label">Transit Phone</label>
                      <input
                        class="shipment-edit__input shipment-edit__input--compact"
                        type="text"
                        [value]="transitPhoneValue"
                        placeholder="Transit phone will fill from recipient"
                        readonly />
                    </div>

                    <div class="shipment-edit__field shipment-edit__field--full">
                      <label for="shipment-pages-edit-shipmenteditpage-field-4" class="shipment-edit__label">Transit Address</label>
                      <app-location-selector
                        [value]="transitAddressValue"
                        (addressChange)="onTransitAddressChange($event)"></app-location-selector>
                    </div>
                  </div>
                  } @if (hasRouteStarted && getCurrentTransitSnapshot(); as transitSnapshot) {
                  <div class="shipment-edit__summary-list">
                    <div class="shipment-edit__summary-item">
                      <span class="shipment-edit__summary-label">Partner</span>
                      <span class="shipment-edit__summary-value">{{ transitSnapshot.partnerName }}</span>
                    </div>
                    <div class="shipment-edit__summary-item">
                      <span class="shipment-edit__summary-label">Recipient</span>
                      <span class="shipment-edit__summary-value">{{ transitSnapshot.recipientName }}</span>
                    </div>
                    <div class="shipment-edit__summary-item">
                      <span class="shipment-edit__summary-label">Phone</span>
                      <span class="shipment-edit__summary-value">{{ transitSnapshot.recipientPhone || "-" }}</span>
                    </div>
                    <div class="shipment-edit__summary-item shipment-edit__summary-item--stacked">
                      <span class="shipment-edit__summary-label">Address</span>
                      <span class="shipment-edit__summary-value"
                        >{{ formatAddress(transitSnapshot.destinationAddress) || "-" }}</span
                      >
                    </div>
                  </div>
                  } @else if (hasRouteStarted) {
                  <div class="shipment-edit__empty">Select a transit partner to build the relay snapshot.</div>
                  }
                </div>
                }
              </div>
              } @if (!hasRouteStarted) {
              <div class="shipment-edit__final-destination-fields">
                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-5" class="shipment-edit__label">Final Partner</label>
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [options]="finalPartnerOptions"
                    [selectedValue]="selectedFinalPartnerId"
                    [minLength]="1"
                    [debounceMs]="300"
                    placeholder="Search final partner..."
                    (searchChange)="searchFinalPartners($event)"
                    (selectionChange)="onFinalPartnerChange($event)"></app-autocomplete>
                </div>

                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-6" class="shipment-edit__label">Final Recipient</label>
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [options]="finalRecipientOptions"
                    [selectedValue]="selectedFinalRecipientId"
                    [disabled]="!selectedFinalPartnerId"
                    [minLength]="0"
                    [debounceMs]="300"
                    placeholder="{{ selectedFinalPartnerId ? 'Search final recipient...' : 'Select final partner first' }}"
                    (searchChange)="searchFinalRecipients($event)"
                    (selectionChange)="onFinalRecipientChange($event)"></app-autocomplete>
                </div>

                @if (finalRecipientsAvailable === false) {
                <div class="shipment-edit__empty">
                  No team leaders are linked to this partner in Relation Manager. Add one there before saving.
                </div>
                }

                <div class="shipment-edit__field shipment-edit__field--compact">
                  <label for="shipment-pages-edit-shipmenteditpage-field-7" class="shipment-edit__label">Phone</label>
                  <input
                    class="shipment-edit__input shipment-edit__input--compact"
                    type="text"
                    [value]="finalPhoneValue"
                    placeholder="Enter final phone..."
                    (input)="onFinalPhoneChange($any($event.target).value)" />
                </div>

                <div class="shipment-edit__field shipment-edit__field--full">
                  <label for="shipment-pages-edit-shipmenteditpage-field-8" class="shipment-edit__label">Address</label>
                  <app-location-selector
                    [value]="finalAddressValue"
                    (addressChange)="onFinalAddressChange($event)"></app-location-selector>
                </div>
              </div>
              } @else if (getCurrentFinalSnapshot(); as finalSnapshot) {
              <div class="shipment-edit__section shipment-edit__section--with-divider">
                <div class="shipment-edit__snapshot-history">
                  <div class="shipment-edit__snapshot-history-header">
                    <span class="shipment-edit__snapshot-history-icon material-symbols-outlined">where_to_vote</span>
                    <div>
                      <span class="shipment-edit__snapshot-history-kicker">Locked delivery snapshot</span>
                      <h2 class="shipment-edit__snapshot-history-title">Final Destination</h2>
                      <p class="shipment-edit__snapshot-history-copy">Saved destination used for the final delivery leg.</p>
                    </div>
                  </div>

                  <div class="shipment-edit__snapshot-history-grid">
                    <div class="shipment-edit__snapshot-history-item">
                      <span class="shipment-edit__snapshot-history-item-icon material-symbols-outlined">apartment</span>
                      <div>
                        <span class="shipment-edit__snapshot-history-label">Partner</span>
                        <strong class="shipment-edit__snapshot-history-value">{{ finalSnapshot.partnerName }}</strong>
                      </div>
                    </div>
                    <div class="shipment-edit__snapshot-history-item">
                      <span class="shipment-edit__snapshot-history-item-icon material-symbols-outlined">person</span>
                      <div>
                        <span class="shipment-edit__snapshot-history-label">Recipient</span>
                        <strong class="shipment-edit__snapshot-history-value">{{ finalSnapshot.recipientName }}</strong>
                      </div>
                    </div>
                    <div class="shipment-edit__snapshot-history-item">
                      <span class="shipment-edit__snapshot-history-item-icon material-symbols-outlined">call</span>
                      <div>
                        <span class="shipment-edit__snapshot-history-label">Phone</span>
                        <strong class="shipment-edit__snapshot-history-value">{{ finalSnapshot.recipientPhone || "-" }}</strong>
                      </div>
                    </div>
                    <div class="shipment-edit__snapshot-history-item shipment-edit__snapshot-history-item--wide">
                      <span class="shipment-edit__snapshot-history-item-icon material-symbols-outlined">location_on</span>
                      <div>
                        <span class="shipment-edit__snapshot-history-label">Address</span>
                        <strong class="shipment-edit__snapshot-history-value"
                          >{{ formatAddress(finalSnapshot.destinationAddress) || "-" }}</strong
                        >
                      </div>
                    </div>
                    <div class="shipment-edit__snapshot-history-item shipment-edit__snapshot-history-item--wide">
                      <span class="shipment-edit__snapshot-history-item-icon material-symbols-outlined">sticky_note_2</span>
                      <div>
                        <span class="shipment-edit__snapshot-history-label">Shipment Note</span>
                        <div
                          class="shipment-edit__snapshot-history-value shipment-edit__readonly-note--snapshot"
                          [innerHTML]="shipment.note || '<p>-</p>'"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              } @if (canEditSnapshot) {
              <div class="shipment-edit__field">
                <label for="shipment-pages-edit-shipmenteditpage-field-9" class="shipment-edit__label">Shipment Note</label>
                <app-rich-text-editor formControlName="note" placeholder="Write shipment note..."></app-rich-text-editor>
              </div>
              }
            </form>
          </app-card>
          } @if (activeStage === "pack") {
          <div class="shipment-edit__pack-rows">
            <app-card class="shipment-edit__context-card--secondary">
              <div class="shipment-edit__section shipment-edit__section--compact">
                <div class="shipment-edit__section-header">
                  <div>
                    <h2 class="shipment-edit__section-title">
                      {{ isDeliveredShipment ? "Request Packing History" : "Unboxed Items" }}
                    </h2>
                    <p class="shipment-edit__section-copy">
                      {{ isDeliveredShipment ? "Review the request quantities that were packed and delivered." : "Focus on what still
                      needs to go into boxes. Remaining quantities are the main number to watch here." }}
                    </p>
                  </div>
                </div>

                <div class="shipment-edit__request-list">
                  @for (request of shipment.requests ?? []; track request.requestId) {
                  <div class="shipment-edit__request-card">
                    <button type="button" class="shipment-edit__request-toggle" (click)="toggleRequestExpanded(request.requestId)">
                      <div class="shipment-edit__request-toggle-copy">
                        <div class="shipment-edit__request-topline">
                          <strong>{{ request.requestNo }}</strong>
                          <span>{{ request.teamLeaderName }}</span>
                        </div>
                        <div class="shipment-edit__request-meta">
                          <span>{{ request.partnerName }}</span>
                          <span
                            >{{ isDeliveredShipment ? "Delivered " + request.totalDeliveredQty : "Remaining " +
                            request.totalRemainingQty }}</span
                          >
                        </div>
                      </div>
                      <span class="shipment-edit__request-toggle-icon material-symbols-outlined">
                        {{ isRequestExpanded(request.requestId) ? "expand_less" : "expand_more" }}
                      </span>
                    </button>

                    @if (isRequestExpanded(request.requestId)) {
                    <div class="shipment-edit__request-items">
                      @for (item of request.items; track item.materialId + '-' + $index) {
                      <div class="shipment-edit__request-item">
                        <div class="shipment-edit__request-item-copy">
                          <strong>{{ item.materialSku || item.materialName }}</strong>
                          @if (item.materialSku && item.materialName) {
                          <span>{{ item.materialName }}</span>
                          }
                        </div>
                        <div class="shipment-edit__request-item-stats">
                          <span class="shipment-edit__request-stat shipment-edit__request-stat--primary"
                            >{{ isDeliveredShipment ? "Delivered" : "Remaining" }}
                            <strong>{{ isDeliveredShipment ? item.deliveredQty : item.qty - item.boxedQty }}</strong></span
                          >
                          <span class="shipment-edit__request-stat">Requested <strong>{{ item.qty }}</strong></span>
                          <span class="shipment-edit__request-stat">Boxed <strong>{{ item.boxedQty }}</strong></span>
                        </div>
                      </div>
                      }
                    </div>
                    }
                  </div>
                  }
                </div>
              </div>
            </app-card>

            <app-card>
              <div class="shipment-edit__section">
                <div class="shipment-edit__section-header">
                  <div>
                    <h2 class="shipment-edit__section-title">{{ packWorkspaceTitle }}</h2>
                    <p class="shipment-edit__section-copy">{{ packWorkspaceCopy }}</p>
                  </div>
                </div>

                @if (isEditableShipment) { @if (!hasActiveBoxWorkspace) {
                <div class="shipment-edit__workspace-empty">
                  <div class="shipment-edit__workspace-empty-copy">
                    <h3>{{ hasBoxes ? "Select an existing box or start a new one" : "Create the first box to start packing" }}</h3>
                    <p>
                      @if (hasBoxes) { Create a fresh box draft here, or choose a saved box from the boxes section below to continue
                      packing. } @else { Create the first box, then assign the remaining request quantities into it one box at a
                      time. }
                    </p>
                  </div>
                  <app-button type="button" (click)="startNewBox()"
                    >{{ hasBoxes ? "Create New Box" : "Create First Box" }}</app-button
                  >
                </div>
                } @else {
                <form [formGroup]="boxForm" class="shipment-edit__box-editor">
                  <div class="shipment-edit__box-block">
                    <div class="shipment-edit__field-grid shipment-edit__field-grid--draft">
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-10" class="shipment-edit__label">Sequence No</label>
                        <input class="shipment-edit__input" type="number" min="1" formControlName="sequenceNo" />
                      </div>
                    </div>
                  </div>

                  <div class="shipment-edit__box-block">
                    <div class="shipment-edit__box-block-header">
                      <h3 class="shipment-edit__box-block-title">Box Note</h3>
                      <p class="shipment-edit__box-block-copy">
                        Keep this short. Add a quick handling or packing note only if it helps the team.
                      </p>
                    </div>

                    <div class="shipment-edit__field shipment-edit__field--box-note">
                      <app-rich-text-editor formControlName="note" placeholder="Write box note..."></app-rich-text-editor>
                    </div>
                  </div>

                  <div class="shipment-edit__box-block">
                    <div class="shipment-edit__box-block-header">
                      <h3 class="shipment-edit__box-block-title">Pack Items Into This Box</h3>
                      <p class="shipment-edit__box-block-copy">
                        Set the quantity you want to place into this box for each remaining material line.
                      </p>
                    </div>

                    @if (allocationRows.length > 0) {
                    <div class="shipment-edit__allocation-list">
                      @for (allocation of allocationRows; track allocation.requestId + '-' + allocation.materialId; let i = $index) {
                      <div class="shipment-edit__allocation-card" [formGroup]="allocationControlAt(i)">
                        <div class="shipment-edit__allocation-copy">
                          <div class="shipment-edit__allocation-topline">
                            <strong>{{ allocation.materialSku || allocation.materialName }}</strong>
                            <span>{{ allocation.requestNo }} · {{ allocation.teamLeaderName }}</span>
                          </div>
                          <div class="shipment-edit__allocation-meta">
                            <span class="shipment-edit__allocation-stat shipment-edit__allocation-stat--primary"
                              >Remaining to box <strong>{{ allocation.availableQty }}</strong></span
                            >
                            <span class="shipment-edit__allocation-stat"
                              >Requested <strong>{{ allocation.requestedQty }}</strong></span
                            >
                            <span class="shipment-edit__allocation-stat"
                              >Already boxed <strong>{{ allocation.boxedQty }}</strong></span
                            >
                          </div>
                        </div>
                        <div class="shipment-edit__allocation-input">
                          <label for="shipment-pages-edit-shipmenteditpage-field-11" class="shipment-edit__label">Pack Qty</label>
                          <input
                            class="shipment-edit__input shipment-edit__input--qty"
                            type="number"
                            min="0"
                            appQtyInput
                            [max]="allocation.availableQty"
                            formControlName="qty" />
                        </div>
                      </div>
                      }
                    </div>
                    } @else {
                    <div class="shipment-edit__allocation-empty">
                      All request quantities are already assigned to boxes. Reopen a saved box below if you need to adjust packed
                      items.
                    </div>
                    }
                  </div>

                  <div class="shipment-edit__box-actions shipment-edit__box-actions--sticky">
                    <div class="shipment-edit__box-actions-copy">Ready to save this box draft once your quantities are set.</div>
                    <div class="shipment-edit__box-actions-buttons">
                      <app-button type="button" variant="secondary" (click)="startNewBox()">Clear Box Draft</app-button>
                      <app-button type="button" [disabled]="isSaving || boxForm.invalid || !hasBoxContent()" (click)="saveBox()">
                        {{ editingBoxId ? "Update Box" : "Add Box" }}
                      </app-button>
                    </div>
                  </div>
                </form>
                } } @else {
                <div class="shipment-edit__readonly-note">Box allocations are locked after dispatch.</div>
                }
              </div>
            </app-card>
          </div>
          } @if (activeStage === "dispatch") {
          <app-card>
            <div class="shipment-edit__section">
              <div class="shipment-edit__section-header">
                <div>
                  <h2 class="shipment-edit__section-title">{{ getDispatchSectionTitle() }}</h2>
                  <p class="shipment-edit__section-copy">{{ getDispatchSectionCopy() }}</p>
                </div>
              </div>

              <form [formGroup]="dispatchForm" class="shipment-edit__section">
                @if (isTransitDispatchRoute) {
                <div class="shipment-edit__dispatch-route">
                  <div
                    class="shipment-edit__route-card"
                    [class.shipment-edit__route-card--active]="currentLeg?.id === transitLeg?.id"
                    [class.shipment-edit__route-card--complete]="transitLeg?.status === 'Delivered'">
                    <div class="shipment-edit__route-card-header">
                      <div>
                        <span class="shipment-edit__route-card-kicker">Leg 1</span>
                        <h3 class="shipment-edit__route-card-title">Transit Drop Point</h3>
                        <p class="shipment-edit__route-card-copy">{{ transitLeg?.destinationInstitutionName || "-" }}</p>
                      </div>
                      <span class="shipment-edit__route-badge">{{ getLegStatusLabel(transitLeg) }}</span>
                    </div>

                    @if (isTransitFirstDispatch) {
                    <div class="shipment-edit__route-card-fields">
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-12" class="shipment-edit__label"
                          >Transit Vendor</label
                        >
                        <app-autocomplete
                          [prefetchOnInit]="true"
                          [options]="vendorOptions"
                          [selectedValue]="selectedDispatchVendorId"
                          [disabled]="shipment.status !== 'Packed'"
                          [minLength]="1"
                          [debounceMs]="300"
                          placeholder="Search transit shipment vendor..."
                          (searchChange)="searchShipmentVendors($event)"
                          (selectionChange)="onDispatchVendorChange($event)"></app-autocomplete>
                      </div>
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-13" class="shipment-edit__label"
                          >Transit Waybill No</label
                        >
                        <input class="shipment-edit__input" type="text" formControlName="waybillNo" />
                      </div>
                    </div>
                    } @else {
                    <div class="shipment-edit__route-card-summary">
                      <div>
                        <span>Vendor</span>
                        <strong>{{ getLegVendorLabel(transitLeg) }}</strong>
                      </div>
                      <div>
                        <span>Waybill</span>
                        <strong>{{ getLegWaybillLabel(transitLeg) }}</strong>
                      </div>
                    </div>
                    } @if (isTransitArrivalPending) {
                    <div class="shipment-edit__route-receipt" [formGroup]="deliverForm">
                      <div>
                        <h4 class="shipment-edit__route-receipt-title">Confirm Transit Arrival</h4>
                        <p class="shipment-edit__route-receipt-copy">
                          Confirm the relay point has received the boxes before opening the final-leg dispatch.
                        </p>
                      </div>
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-14" class="shipment-edit__label">Received By</label>
                        <input class="shipment-edit__input" type="text" formControlName="receivedBy" />
                      </div>
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-15" class="shipment-edit__label"
                          >Transit Receipt Note</label
                        >
                        <app-rich-text-editor
                          formControlName="note"
                          placeholder="Write transit receipt note..."></app-rich-text-editor>
                      </div>
                      <app-button type="button" [disabled]="isSaving" (click)="deliverShipment()"
                        >Confirm Transit Arrival</app-button
                      >
                    </div>
                    }
                  </div>

                  <div
                    class="shipment-edit__route-card"
                    [class.shipment-edit__route-card--active]="currentLeg?.id === finalLeg?.id"
                    [class.shipment-edit__route-card--complete]="finalLeg?.status === 'Delivered'">
                    <div class="shipment-edit__route-card-header">
                      <div>
                        <span class="shipment-edit__route-card-kicker">Leg 2</span>
                        <h3 class="shipment-edit__route-card-title">Final Destination</h3>
                        <p class="shipment-edit__route-card-copy">{{ finalLeg?.destinationInstitutionName || "-" }}</p>
                      </div>
                      <span class="shipment-edit__route-badge">{{ getLegStatusLabel(finalLeg) }}</span>
                    </div>

                    @if (isTransitFirstDispatch) {
                    <div class="shipment-edit__route-card-fields">
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-16" class="shipment-edit__label"
                          >Final Vendor Required Before Stock Leaves</label
                        >
                        <app-autocomplete
                          [prefetchOnInit]="true"
                          [options]="vendorOptions"
                          [selectedValue]="selectedFinalLegVendorId"
                          [disabled]="shipment.status !== 'Packed'"
                          [minLength]="1"
                          [debounceMs]="300"
                          placeholder="Search final shipment vendor..."
                          (searchChange)="searchShipmentVendors($event)"
                          (selectionChange)="onFinalLegVendorChange($event)"></app-autocomplete>
                      </div>
                    </div>
                    } @else if (isFinalLegDispatch) {
                    <div class="shipment-edit__route-card-fields">
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-17" class="shipment-edit__label"
                          >Locked Final Vendor</label
                        >
                        <input class="shipment-edit__input" type="text" [value]="getFinalLegVendorLabel()" disabled />
                      </div>
                      <div class="shipment-edit__field">
                        <label for="shipment-pages-edit-shipmenteditpage-field-18" class="shipment-edit__label"
                          >Final Waybill No</label
                        >
                        <input class="shipment-edit__input" type="text" formControlName="waybillNo" />
                      </div>
                    </div>
                    } @else {
                    <div class="shipment-edit__route-card-summary">
                      <div>
                        <span>Vendor</span>
                        <strong>{{ getLegVendorLabel(finalLeg) }}</strong>
                      </div>
                      <div>
                        <span>Waybill</span>
                        <strong>{{ getLegWaybillLabel(finalLeg) }}</strong>
                      </div>
                    </div>
                    }
                  </div>
                </div>
                } @else { @if (isDeliveredShipment) {
                <div class="shipment-edit__route-card-summary">
                  <div>
                    <span>Vendor</span>
                    <strong>{{ getDispatchVendorLabel() }}</strong>
                  </div>
                  <div>
                    <span>Waybill</span>
                    <strong>{{ getDispatchWaybillLabel() }}</strong>
                  </div>
                </div>
                } @else {
                <div class="shipment-edit__field-grid">
                  <div class="shipment-edit__field">
                    <label for="shipment-pages-edit-shipmenteditpage-field-19" class="shipment-edit__label"
                      >{{ currentLeg?.type === "Transit" ? "Transit Vendor" : isFinalLegDispatch ? "Final Vendor" : "Vendor"
                      }}</label
                    >
                    @if (isFinalLegDispatch) {
                    <input class="shipment-edit__input" type="text" [value]="getFinalLegVendorLabel()" disabled />
                    } @else {
                    <app-autocomplete
                      [prefetchOnInit]="true"
                      [options]="vendorOptions"
                      [selectedValue]="selectedDispatchVendorId"
                      [disabled]="shipment.status !== 'Packed'"
                      [minLength]="1"
                      [debounceMs]="300"
                      placeholder="Search shipment vendor..."
                      (searchChange)="searchShipmentVendors($event)"
                      (selectionChange)="onDispatchVendorChange($event)"></app-autocomplete>
                    }
                  </div>
                  @if (isTransitFirstDispatch) {
                  <div class="shipment-edit__field">
                    <label for="shipment-pages-edit-shipmenteditpage-field-20" class="shipment-edit__label">Final Vendor</label>
                    <app-autocomplete
                      [prefetchOnInit]="true"
                      [options]="vendorOptions"
                      [selectedValue]="selectedFinalLegVendorId"
                      [disabled]="shipment.status !== 'Packed'"
                      [minLength]="1"
                      [debounceMs]="300"
                      placeholder="Search final shipment vendor..."
                      (searchChange)="searchShipmentVendors($event)"
                      (selectionChange)="onFinalLegVendorChange($event)"></app-autocomplete>
                  </div>
                  }
                  <div class="shipment-edit__field">
                    <label for="shipment-pages-edit-shipmenteditpage-field-21" class="shipment-edit__label"
                      >{{ currentLeg?.type === "Transit" ? "Transit Waybill No" : "Waybill No" }}</label
                    >
                    <input class="shipment-edit__input" type="text" formControlName="waybillNo" />
                  </div>
                </div>
                } } @if (canEditDispatchDocumentation) {
                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-22" class="shipment-edit__label">Dispatch Note</label>
                  <app-rich-text-editor formControlName="note" placeholder="Write dispatch note..."></app-rich-text-editor>
                </div>

                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-23" class="shipment-edit__label"
                    >Dispatch Attachments</label
                  >
                  <app-file-uploader
                    formControlName="attachments"
                    [folder]="attachmentFolder"
                    [multiple]="true"
                    viewMode="thumbnail"
                    [disabled]="isDispatchAttachmentUploaderDisabled()"
                    label="Upload shipment dispatch attachments"
                    [hint]="getDispatchAttachmentHint()"
                    emptyStateTitle="No dispatch attachments added"
                    emptyStateDescription="Uploaded dispatch attachments will appear here."
                    (filesChange)="onDispatchAttachmentsChange($event)"></app-file-uploader>
                </div>

                @if (shipment.status === "Packed" && hasUnboxedRemaining()) {
                <div class="shipment-edit__warning">
                  Some request quantities are still unboxed. Finish packing before dispatch so stock moves out cleanly.
                </div>
                } @if (shipment.status === "Packed" && hasBoxesNeedingMeasurement) {
                <div class="shipment-edit__warning">
                  One or more saved boxes still need dimensions and weight. Measure every box before dispatch.
                </div>
                } @if (shipment.status === "Packed") {
                <app-button type="button" [disabled]="!canSubmitDispatch" (click)="dispatchShipment()"
                  >{{ getDispatchButtonLabel() }}</app-button
                >
                } @else {
                <app-button type="button" [disabled]="isSaving || isDispatchAttachmentSaving" (click)="saveDispatchDetails()">
                  {{ isSaving ? "Saving..." : "Save Dispatch Details" }}
                </app-button>
                } } @else {
                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-24" class="shipment-edit__label">Dispatch Note</label>
                  <div class="shipment-edit__readonly-note" [innerHTML]="shipment.note || '<p>-</p>'"></div>
                </div>

                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-25" class="shipment-edit__label"
                    >Dispatch Attachments</label
                  >
                  @if (shipment.attachments.length > 0) {
                  <div class="shipment-edit__attachment-list">
                    @for (attachment of shipment.attachments; track attachment.id) {
                    <a
                      class="shipment-edit__attachment-link"
                      [href]="attachment.publicUrl"
                      target="_blank"
                      rel="noopener noreferrer">
                      <span class="shipment-edit__attachment-name">{{ attachment.originalName }}</span>
                      <span class="shipment-edit__attachment-meta">{{ attachment.size }} bytes</span>
                    </a>
                    }
                  </div>
                  } @else {
                  <div class="shipment-edit__readonly-note">No dispatch attachments.</div>
                  }
                </div>

                <div class="shipment-edit__readonly-note">
                  Vendor {{ getDispatchVendorLabel() }} · Waybill {{ getDispatchWaybillLabel() }} · Dispatched on {{
                  shipment.dispatchedAt || "-" }}.
                </div>
                }
              </form>
            </div>
          </app-card>
          } @if (activeStage === "delivery") {
          <app-card>
            <div class="shipment-edit__section">
              <div class="shipment-edit__section-header">
                <div>
                  <h2 class="shipment-edit__section-title">{{ getDeliverySectionTitle() }}</h2>
                  <p class="shipment-edit__section-copy">{{ getDeliverySectionCopy() }}</p>
                </div>
              </div>

              <form [formGroup]="deliverForm" class="shipment-edit__section">
                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-26" class="shipment-edit__label">Received By</label>
                  <input class="shipment-edit__input" type="text" formControlName="receivedBy" />
                </div>
                <div class="shipment-edit__field">
                  <label for="shipment-pages-edit-shipmenteditpage-field-27" class="shipment-edit__label">Delivery Note</label>
                  <app-rich-text-editor formControlName="note" placeholder="Write delivery note..."></app-rich-text-editor>
                </div>

                @if (shipment.status === "Dispatched") {
                <app-button type="button" (click)="deliverShipment()">Confirm Delivery</app-button>
                } @else {
                <div class="shipment-edit__readonly-note">
                  Delivered to {{ shipment.receivedBy || "-" }} on {{ shipment.deliveredAt || "-" }}.
                </div>
                }
              </form>
            </div>
          </app-card>
          }
        </div>

        <div class="shipment-edit__aside">
          @if (activeStage === "pack") {
          <app-card class="shipment-edit__context-card--secondary">
            <app-summary-panel
              title="Packing Summary"
              subtitle="Keep the key numbers and current box context visible while packing."
              [rows]="packingSummaryRows"></app-summary-panel>
          </app-card>
          } @else {
          <app-card>
            <app-summary-panel
              title="Operations Summary"
              subtitle="A compact view of how far this shipment has moved."
              [rows]="operationsSummaryRows"></app-summary-panel>
          </app-card>
          }
        </div>

        @if (activeStage === "pack") {
        <app-card class="shipment-edit__pack-fullwidth">
          <div class="shipment-edit__section">
            <div class="shipment-edit__section-header">
              <div>
                <h2 class="shipment-edit__section-title">Measure & Label Saved Boxes</h2>
                <p class="shipment-edit__section-copy">
                  Once a box is physically packed, save its measurements here. Labels unlock after measurement.
                </p>
              </div>
              <div class="shipment-edit__actions">
                <app-button type="button" variant="secondary" [disabled]="!canOpenAllLabels()" (click)="openAllLabelsPdf()">
                  Open Labels
                </app-button>
              </div>
            </div>

            @if (hasSavedBoxes) {
            <div class="shipment-edit__saved-box-table">
              <app-table
                [data]="savedBoxesData"
                [columns]="savedBoxColumns"
                [config]="savedBoxConfig"
                [pagination]="savedBoxesPagination"></app-table>
            </div>
            } @else {
            <div class="shipment-edit__info-banner">
              <span class="shipment-edit__info-banner-icon material-symbols-outlined">info</span>
              <span>No boxes yet. Start by creating the first box in the section above, then saved boxes will appear here.</span>
            </div>
            }
          </div>
        </app-card>
        }
      </div>
      }
    </div>
  </div>
</div>

<ng-template #savedBoxExpandedRow let-box>
  <app-shipment-box-expanded-row [box]="box"></app-shipment-box-expanded-row>
</ng-template>
